// FamilyManagementScreen.js
// RALPH FIX 02 — Built by Claude, 2026-02-23
// Calls existing backend endpoints: GET/POST /api/family/members,
// PUT/DELETE /api/family/members/<id>

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  MenuItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Chip,
  Avatar,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import { supabase } from '../supabaseClient';   // adjust path if yours differs

// ── Config ─────────────────────────────────────────────────────────────────
// Matches what the rest of the app uses. Change if your env var name differs.
const BASE_URL = '/api';

const RELATIONSHIPS = ['Spouse', 'Partner', 'Child', 'Parent', 'Sibling', 'Other'];

const EMPTY_FORM = {
  name: '',
  age: '',
  relationship: '',
  emergency_contact: '',
  photo_url: '',
};

// ── Auth helper ────────────────────────────────────────────────────────────
async function getAuthHeaders() {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('You must be logged in.');
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${session.access_token}`,
  };
}

// ── Component ──────────────────────────────────────────────────────────────
export default function FamilyManagementScreen() {
  const [members, setMembers]             = useState([]);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState('');
  const [success, setSuccess]             = useState('');

  // Add / Edit dialog
  const [dialogOpen, setDialogOpen]       = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [form, setForm]                   = useState(EMPTY_FORM);
  const [saving, setSaving]               = useState(false);
  const [formError, setFormError]         = useState('');

  // Delete confirmation dialog
  const [deleteOpen, setDeleteOpen]       = useState(false);
  const [deletingMember, setDeletingMember] = useState(null);
  const [deleting, setDeleting]           = useState(false);

  // ── Fetch list ────────────────────────────────────────────────
  const fetchMembers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${BASE_URL}/family/members`, { headers });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || `Server error ${res.status}`);
      }
      const data = await res.json();
      // Backend may return [{...}, ...] or { members: [...] }
      setMembers(Array.isArray(data) ? data : (data.members || []));
    } catch (e) {
      setError(e.message || 'Failed to load family members.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMembers(); }, [fetchMembers]);

  // ── Open dialog helpers ───────────────────────────────────────
  const openAddDialog = () => {
    setEditingMember(null);
    setForm(EMPTY_FORM);
    setFormError('');
    setDialogOpen(true);
  };

  const openEditDialog = (member) => {
    setEditingMember(member);
    setForm({
      name:              member.name              || '',
      age:               member.age != null ? String(member.age) : '',
      relationship:      member.relationship      || '',
      emergency_contact: member.emergency_contact || '',
      photo_url:         member.photo_url         || '',
    });
    setFormError('');
    setDialogOpen(true);
  };

  const closeDialog = () => { setDialogOpen(false); setFormError(''); };

  // ── Save (add or update) ──────────────────────────────────────
  const handleSave = async () => {
    if (!form.name.trim()) { setFormError('Name is required.'); return; }
    setSaving(true);
    setFormError('');
    try {
      const headers = await getAuthHeaders();
      const body = {
        name:              form.name.trim(),
        age:               form.age ? parseInt(form.age, 10) : null,
        relationship:      form.relationship  || null,
        emergency_contact: form.emergency_contact.trim() || null,
        photo_url:         form.photo_url.trim()         || null,
      };

      const url = editingMember
        ? `${BASE_URL}/family/members/${editingMember.id}`
        : `${BASE_URL}/family/members`;
      const method = editingMember ? 'PUT' : 'POST';

      const res = await fetch(url, { method, headers, body: JSON.stringify(body) });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `Server error ${res.status}`);
      }

      showSuccess(editingMember ? 'Family member updated.' : 'Family member added.');
      setDialogOpen(false);
      await fetchMembers();
    } catch (e) {
      setFormError(e.message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────────
  const openDeleteConfirm = (member) => {
    setDeletingMember(member);
    setDeleteOpen(true);
  };

  const handleDelete = async () => {
    if (!deletingMember) return;
    setDeleting(true);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${BASE_URL}/family/members/${deletingMember.id}`, {
        method: 'DELETE',
        headers,
      });
      if (!res.ok) throw new Error(`Server error ${res.status}`);
      showSuccess('Family member removed.');
      setDeleteOpen(false);
      setDeletingMember(null);
      await fetchMembers();
    } catch (e) {
      setError(e.message || 'Failed to delete. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const showSuccess = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(''), 4000);
  };

  // ── Render ────────────────────────────────────────────────────
  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', px: 2, py: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>Family Members</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openAddDialog}>
          Add Member
        </Button>
      </Box>

      {/* Feedback banners */}
      {error   && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {/* Content */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>

      ) : members.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <PersonIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary" gutterBottom>No family members yet.</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Add family members to track their dietary needs and allergies.
          </Typography>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={openAddDialog}>
            Add Your First Member
          </Button>
        </Box>

      ) : (
        members.map((member) => (
          <Card key={member.id} sx={{ mb: 2 }}>
            <CardContent sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <Avatar
                src={member.photo_url || undefined}
                sx={{ width: 48, height: 48, bgcolor: 'primary.main', flexShrink: 0 }}
              >
                {member.photo_url ? null : (member.name?.[0]?.toUpperCase() || <PersonIcon />)}
              </Avatar>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle1" fontWeight={600} noWrap>
                  {member.name}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                  {member.relationship && (
                    <Chip label={member.relationship} size="small" variant="outlined" />
                  )}
                  {member.age != null && (
                    <Chip label={`Age ${member.age}`} size="small" variant="outlined" />
                  )}
                </Box>
                {member.emergency_contact && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }} noWrap>
                    📞 {member.emergency_contact}
                  </Typography>
                )}
              </Box>

              <Box sx={{ flexShrink: 0 }}>
                <IconButton size="small" onClick={() => openEditDialog(member)} title="Edit">
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" color="error" onClick={() => openDeleteConfirm(member)} title="Remove">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </CardContent>
          </Card>
        ))
      )}

      {/* ── Add / Edit Dialog ──────────────────────────────────── */}
      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>
          {editingMember ? 'Edit Family Member' : 'Add Family Member'}
        </DialogTitle>

        <DialogContent sx={{ pt: '16px !important' }}>
          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}

          <TextField
            label="Full Name"
            required
            fullWidth
            autoFocus
            value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            sx={{ mb: 2 }}
          />

          <TextField
            label="Relationship"
            select
            fullWidth
            value={form.relationship}
            onChange={e => setForm(f => ({ ...f, relationship: e.target.value }))}
            sx={{ mb: 2 }}
          >
            <MenuItem value=""><em>Select relationship</em></MenuItem>
            {RELATIONSHIPS.map(r => (
              <MenuItem key={r} value={r}>{r}</MenuItem>
            ))}
          </TextField>

          <TextField
            label="Age"
            type="number"
            fullWidth
            value={form.age}
            onChange={e => setForm(f => ({ ...f, age: e.target.value }))}
            inputProps={{ min: 0, max: 130 }}
            sx={{ mb: 2 }}
          />

          <TextField
            label="Emergency Contact"
            fullWidth
            placeholder="Name and phone number"
            value={form.emergency_contact}
            onChange={e => setForm(f => ({ ...f, emergency_contact: e.target.value }))}
            sx={{ mb: 2 }}
          />

          <TextField
            label="Photo URL (optional)"
            fullWidth
            value={form.photo_url}
            onChange={e => setForm(f => ({ ...f, photo_url: e.target.value }))}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDialog} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ minWidth: 120 }}>
            {saving
              ? <CircularProgress size={20} color="inherit" />
              : editingMember ? 'Save Changes' : 'Add Member'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Confirmation Dialog ─────────────────────────── */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Remove Family Member?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove <strong>{deletingMember?.name}</strong>?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleting}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting} sx={{ minWidth: 100 }}>
            {deleting ? <CircularProgress size={20} color="inherit" /> : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
